import React, { PureComponent, Fragment } from 'react'
import cx from 'classnames'
import { isURL } from 'validator'
import { Button, Input, Form, Div, P } from '@drifthq/tide-core'
import { UploadArea } from '@drifthq/tide-file-upload'
import { pathOr, isEmpty } from 'ramda'
import { track } from 'utils/trackingUtils'
import { debounce } from 'debounce'

import { uploadFile } from 'modules/file/api'


import { createEntityPatchOperation } from 'utils/operations'

import ProgressMeterWithLogo from 'components/ProgressMeterWithLogo'
import { OnboardingHeader } from 'components/OnboardingHeader'
import { DisabledButton } from "components/OnboardingDisabledButton"

import './style.css'

const DIRECTORY_URL_ERROR_TEXT = "Whoops! That url is already taken"
const DEBOUNCE_IN_MS = 400

const DISABLED_TOOLTIP = <Div>Make sure you’ve filled out your<br/>company name, URL, and directory URL</Div>

const LogoUploader = ({ onSizeError, onTypeError, onReadError, onSave, previewUrl, uploadError }) => (
  <Fragment>
    <Div className="onboarding-company-step-upload-text">
      <span>Upload Your Team’s Logo</span>
      <P>Your logo should be less than 500px x 300px. Logos should be a .jpg, .png, or .gif to work properly in emails.</P>
    </Div>
    <UploadArea className="onboarding-company-step--logo"
          onSizeError={onSizeError}
          onTypeError={onTypeError}
          onReadError={onReadError}
          handleUploadFile={uploadFile}
          handleSaveFile={onSave}
          previewUrl={previewUrl}
        />
    { uploadError && <span className="onboarding-company-step--logo-error">{uploadError}</span> }
  </Fragment>
)

class OnboardingCompanyInfoStep extends PureComponent {

  static defaultProps = {
    companyName: ''
  }

  constructor(props) {
    super(props)
    this.state = {
      companyName: props.companyName,
      companyWebsiteUrl: '',
      companyWebsiteUrlValid: null,
      companyDirectoryUrl: props.directoryUrl,
      companyLogo: '',
      uploadError: null,
    }
  }

  componentDidMount() {

  }

  debounceCheckDirectoryUrl = debounce((url) => {
    const { actions } = this.props
    actions.checkUniqueHandle(url)
  }, DEBOUNCE_IN_MS)

  areFieldsEmpty = () => !this.state.companyName || !this.state.companyWebsiteUrl

  isValidDirectoryUrl = () => this.props.uniqueHandle && !!this.state.companyDirectoryUrl

  isFormInvalid = () => this.areFieldsEmpty() || !isURL(this.state.companyWebsiteUrl)

  onSubmit = () => {
    const { companyName, companyWebsiteUrl, companyDirectoryUrl, companyLogo } = this.state
    const { onContinue, actions } = this.props

    const submitOperations = [
      createEntityPatchOperation({ value: companyName, property: 'organizationName' }),
      createEntityPatchOperation({ value: companyWebsiteUrl, property: 'organizationUrl' }),
      createEntityPatchOperation({ value: companyLogo, property: 'theme.logoUrl' })
    ]

    if (!!companyLogo) {
      const { name } = this.props
      track(`${name} Company - Logo Uploaded`)
    }

    actions.updateEmbed(submitOperations)
    actions.updateOrg({ handle: companyDirectoryUrl })
    onContinue && onContinue()
  }

  onCompanyNameChanged = (event) => {
    const changedValue = pathOr('', ['target', 'value'], event)
    this.setState({ companyName: changedValue })
  }

  onCompanyWebsiteUrlChanged = (event) => {
    const changedValue = pathOr('', ['target', 'value'], event)
    const isValid = isURL(changedValue)

    this.setState({ companyWebsiteUrl: changedValue, companyWebsiteUrlValid: isValid })
  }

  onCompanyDirectoryUrlChanged = (event) => {
    const changedValue = pathOr('', ['target', 'value'], event)

    this.setState({ companyDirectoryUrl: changedValue }, () => this.debounceCheckDirectoryUrl(changedValue))
  }

  onLogoSizeError = () => {
    this.setState({ uploadError: 'Your logo exceeds our maximum file size of 20MB' })
  }

  onLogoTypeError = () => {
    this.setState({ uploadError: 'Your logo type is not supported' })
  }

  onLogoReadError = () => {
    this.setState({ uploadError: 'We couldn’t read the file. Please try again.' })
  }

  handleSaveLogo = (logoUrl) => {
    this.setState({ companyLogo: logoUrl, uploadError: null })
  }

  render() {
    const { index, stepCount, uniqueHandle } = this.props
    const {
      companyName,
      companyWebsiteUrl,
      companyWebsiteUrlValid,
      companyDirectoryUrl,
      companyLogo,
      uploadError } = this.state

    return (
      <Div className="onboarding-company-step">
        <ProgressMeterWithLogo currentLength={index} maxLength={stepCount - 1}/>
        <OnboardingHeader
            title="Tell us about your company"
            description="Your company information will help customers recognize and connect with your brand!"
          />
        <Form className="onboarding-company-step--form" onSubmit={this.onSubmit}>
          <Input className="onboarding-company-step--name-input" label="Company Name" value={companyName} onChange={this.onCompanyNameChanged}/>
          <Input className={cx("onboarding-company-step--website-url-input", { 'invalid-field-input': companyWebsiteUrl !== '' && !companyWebsiteUrlValid })}
          label="Company Website URL"
          value={companyWebsiteUrl}
          onChange={this.onCompanyWebsiteUrlChanged}/>
          <Div className="onboarding-company-directory">
            <Input className={cx({ 'invalid-field-input': !this.isValidDirectoryUrl()})}
              label="Company Directory URL"
              value={companyDirectoryUrl}
              onChange={this.onCompanyDirectoryUrlChanged}/>
            <Div className='onboarding-company-directory-suffix'>.drift.com</Div>
          </Div>
          {!uniqueHandle && !isEmpty(companyDirectoryUrl) && <Div className="onboarding-company-directory--error">{DIRECTORY_URL_ERROR_TEXT}</Div>}
          <LogoUploader onSizeError={this.onLogoSizeError}
            onTypeError={this.onLogoTypeError}
            onReadError={this.onLogoReadError}
            onSave={this.handleSaveLogo}
            previewUrl={companyLogo}
            uploadError={uploadError}/>
            { this.isFormInvalid() && <DisabledButton buttonClassName="onboarding-company-step--submit" toolTipText={DISABLED_TOOLTIP}/> }
            { !this.isFormInvalid() &&
              <Button className="onboarding-company-step--submit"
                size="large"
                submit
                onClick={this.onSubmit}>
                Next
              </Button>
            }
        </Form>
      </Div>
    )
  }
}

export default OnboardingCompanyInfoStep



// WEBPACK FOOTER //
// ./src/layouts/OnboardingCompanyInfo/index.js